import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface SnapshotRowContextType {
  rowHeights: Map<number, number>;
  headerHeights: Map<number, number>;
  updateHeight: (rowIndex: number, cardIndex: number, height: number) => void;
  updateHeaderHeight: (rowIndex: number, cardIndex: number, height: number) => void;
  removeCard: (rowIndex: number, cardIndex: number) => void;
}

const SnapshotRowContext = createContext<SnapshotRowContextType | null>(null);

interface SnapshotRowProviderProps {
  children: React.ReactNode;
}

export const SnapshotRowProvider: React.FC<SnapshotRowProviderProps> = ({ children }) => {
  // Map to store heights: Map<rowIndex, Map<cardIndex, height>>
  const [cardHeights, setCardHeights] = useState<Map<number, Map<number, number>>>(new Map());
  const [headerCardHeights, setHeaderCardHeights] = useState<Map<number, Map<number, number>>>(new Map());
  const [rowHeights, setRowHeights] = useState<Map<number, number>>(new Map());
  const [headerHeights, setHeaderHeights] = useState<Map<number, number>>(new Map());

  const updateHeight = useCallback((rowIndex: number, cardIndex: number, height: number) => {
    setCardHeights(prev => {
      const newHeights = new Map(prev);
      if (!newHeights.has(rowIndex)) {
        newHeights.set(rowIndex, new Map());
      }
      newHeights.get(rowIndex)!.set(cardIndex, height);
      return newHeights;
    });
  }, []);

  const updateHeaderHeight = useCallback((rowIndex: number, cardIndex: number, height: number) => {
    setHeaderCardHeights(prev => {
      const newHeights = new Map(prev);
      if (!newHeights.has(rowIndex)) {
        newHeights.set(rowIndex, new Map());
      }
      newHeights.get(rowIndex)!.set(cardIndex, height);
      return newHeights;
    });
  }, []);

  const removeCard = useCallback((rowIndex: number, cardIndex: number) => {
    setCardHeights(prev => {
      const newHeights = new Map(prev);
      if (newHeights.has(rowIndex)) {
        const rowMap = newHeights.get(rowIndex)!;
        rowMap.delete(cardIndex);
        if (rowMap.size === 0) {
          newHeights.delete(rowIndex);
        }
      }
      return newHeights;
    });

    setHeaderCardHeights(prev => {
      const newHeights = new Map(prev);
      if (newHeights.has(rowIndex)) {
        const rowMap = newHeights.get(rowIndex)!;
        rowMap.delete(cardIndex);
        if (rowMap.size === 0) {
          newHeights.delete(rowIndex);
        }
      }
      return newHeights;
    });
  }, []);

  // Calculate maximum height for each row whenever cardHeights changes
  useEffect(() => {
    const newRowHeights = new Map<number, number>();
    
    cardHeights.forEach((cardsInRow, rowIndex) => {
      const maxHeight = Math.max(...Array.from(cardsInRow.values()));
      newRowHeights.set(rowIndex, maxHeight);
    });

    setRowHeights(newRowHeights);
  }, [cardHeights]);

  // Calculate maximum header height for each row whenever headerCardHeights changes
  useEffect(() => {
    const newHeaderHeights = new Map<number, number>();
    
    headerCardHeights.forEach((cardsInRow, rowIndex) => {
      const maxHeight = Math.max(...Array.from(cardsInRow.values()));
      newHeaderHeights.set(rowIndex, maxHeight);
    });

    setHeaderHeights(newHeaderHeights);
  }, [headerCardHeights]);

  return (
    <SnapshotRowContext.Provider value={{ 
      rowHeights, 
      headerHeights,
      updateHeight, 
      updateHeaderHeight,
      removeCard 
    }}>
      {children}
    </SnapshotRowContext.Provider>
  );
};

export const useSnapshotRow = () => {
  const context = useContext(SnapshotRowContext);
  if (!context) {
    throw new Error('useSnapshotRow must be used within a SnapshotRowProvider');
  }
  return context;
}; 